import { useEffect, useMemo, useState } from 'react';
import { ActiveThreats } from './ActiveThreats';
import { AlertHistory, OobConfirmations } from './IncidentExtras';
import { Link } from 'react-router-dom';
import {
  ROLE_LABEL,
  ledgerCustody,
  ledgerEntries,
  ledgerExport,
  ledgerProof,
  ledgerVerify,
  oversightGuardianSignals,
  type FacilitySearchRow,
  type LedgerCustodyEntry,
  type LedgerEntryKind,
  type LedgerEntryRow,
  type LedgerRangeVerification,
} from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { sx } from '../../ui/designStyles';
import { useReadOnlyReason } from '../../ui/ReadOnly';
import { Act, PLATFORM_SCOPE, canonicalJson, downloadJson, isFacilityAdmin, localClock, sha256Hex, useApi, useNow } from './common';
import { FacilitySearch } from './FacilitySearch';
import type { LedgerIntent } from './OversightPage';
import { SecurityAudit } from './SecurityAudit';
import { BuildTagged } from '../../ui/buildLabels';
import { AboutButton, AboutThis } from '../../ui/AboutThis';

// design tlVals: KC
const KC: Record<LedgerEntryKind, [string, string]> = {
  Decision: ['#264C7E', 'rgba(47,92,151,.12)'],
  Override: ['#B3261E', 'rgba(179,38,30,.10)'],
  Policy: ['#22272E', 'rgba(34,39,46,.09)'],
  Escalation: ['#264C7E', 'rgba(47,92,151,.12)'],
  'Care log': ['#1F6B52', 'rgba(31,107,82,.10)'],
};
const kStyle = (k: LedgerEntryKind) =>
  'flex:0 0 auto;display:inline-flex;align-items:center;height:22px;padding:0 7px;border-radius:5px;font-size:14px;font-weight:800;letter-spacing:.05em;color:' + (KC[k] ?? KC.Policy)[0] + ';background:' + (KC[k] ?? KC.Policy)[1] + ';';
const short = (h: string) => h.slice(0, 6) + '…' + h.slice(-4);
const RANGES = ['Last hour', 'Last 24 hours', 'Last 7 days', 'Since go-live'] as const;
type RangeName = (typeof RANGES)[number];
const RANGE_MS: Record<RangeName, number | null> = { 'Last hour': 3_600_000, 'Last 24 hours': 86_400_000, 'Last 7 days': 7 * 86_400_000, 'Since go-live': null };
const REASONS = ['Legal hold', 'State survey', 'Incident review', 'Family request', 'Internal audit'];
type KindFilter = 'All' | 'Decision' | 'People' | 'Escalation';
const segBtn = (on: boolean) => 'display:inline-flex;align-items:center;min-height:34px;padding:0 12px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' + (on ? 'background:#22272E;color:#FCFAF4;' : 'color:#3C454F;');

/** The ledger sub-tabs (design ledTabs): Decision chain and Security audit. */
export function LedgerTabs({ sub, onSub }: { sub: 'chain' | 'audit'; onSub: (s: 'chain' | 'audit') => void }) {
  return (
    <span role="tablist" aria-label="Truth Ledger" style={sx('display:inline-flex;border:1px solid rgba(34,39,46,.16);border-radius:9px;overflow:hidden;')}>
      {(
        [
          ['chain', 'Decision chain'],
          ['audit', 'Security audit'],
        ] as const
      ).map(([k, n]) => (
        <button key={k} type="button" role="tab" aria-selected={sub === k} className="dlink" style={sx(segBtn(sub === k))} onClick={() => onSub(k)}>
          {n}
        </button>
      ))}
    </span>
  );
}

/** "02:00 UTC" today, "Yesterday", "3 days ago" (the custody log's when). */
function whenText(iso: string, now: number): string {
  const d = new Date(iso);
  const day = (t: number) => Math.floor(t / 86_400_000);
  const n = day(now) - day(d.getTime());
  if (n <= 0) return ('0' + d.getUTCHours()).slice(-2) + ':' + ('0' + d.getUTCMinutes()).slice(-2) + ' UTC';
  if (n === 1) return 'Yesterday';
  return n + ' days ago';
}
function rangeWords(r: LedgerCustodyEntry['range']): string {
  if (!r || !r.to) return '';
  if (!r.from || Date.parse(r.from) <= 0) return 'since go-live';
  const span = Date.parse(r.to) - Date.parse(r.from);
  if (span <= 3_700_000) return 'last hour';
  if (span <= 90_000_000) return 'last 24 hours';
  if (span <= 8 * 86_400_000) return 'last 7 days';
  return new Date(r.from).toISOString().slice(0, 10) + ' to ' + new Date(r.to).toISOString().slice(0, 10);
}
function custodyWhat(c: LedgerCustodyEntry): string {
  if (c.label) return c.label;
  const rw = rangeWords(c.range);
  if (c.action === 'verify') return 'Chain verified' + (rw ? ' · ' + rw : '');
  if (c.action === 'export') return 'Evidence exported' + (rw ? ' · ' + rw : '');
  if (c.action === 'proof') return 'Proof issued' + (c.entryId ? ' · ' + c.entryId.slice(0, 8) : '');
  return c.action;
}
function custodyWho(c: LedgerCustodyEntry): string {
  if (c.who) return c.who;
  const role = c.actor.role ? (ROLE_LABEL as Record<string, string>)[c.actor.role] ?? c.actor.role : null;
  return [c.actor.name ?? 'Automatic', role, c.recipient ? 'for ' + c.recipient : null].filter(Boolean).join(' · ');
}

export function TruthLedger({ sub, onSub, intent, onIntentUsed }: { sub: 'chain' | 'audit'; onSub: (s: 'chain' | 'audit') => void; intent: LedgerIntent; onIntentUsed: () => void }) {
  if (sub === 'audit') {
    return (
      <div data-screen-label="Truth Ledger" style={sx('flex:1 1 auto;min-height:0;display:flex;flex-direction:column;gap:9px;min-width:0;')}>
        <SecurityAudit tabs={<LedgerTabs sub={sub} onSub={onSub} />} />
      </div>
    );
  }
  return <DecisionChain onSub={onSub} intent={intent} onIntentUsed={onIntentUsed} />;
}

function DecisionChain({ onSub, intent, onIntentUsed }: { onSub: (s: 'chain' | 'audit') => void; intent: LedgerIntent; onIntentUsed: () => void }) {
  const { role } = useConsoleRole();
  const { client, session } = useSession();
  const roReason = useReadOnlyReason();
  const fa = isFacilityAdmin(role);
  const wide = !fa;
  const canExport = role !== 'PARTNER';
  const now = useNow(30_000);
  const [range, setRange] = useState<RangeName>('Last hour');
  const [fac, setFac] = useState<FacilitySearchRow | null>(null);
  const [kf, setKf] = useState<KindFilter>('All');
  const [q, setQ] = useState(intent.query ?? '');
  const [selId, setSelId] = useState<string | null>(null);
  const [ver, setVer] = useState<{ s: 'running'; range: RangeName } | { s: 'done'; range: RangeName; at: string; r: LedgerRangeVerification } | { s: 'error'; msg: string } | null>(null);
  const [exportOpen, setExportOpen] = useState(!!intent.exportOpen);
  const [reason, setReason] = useState(REASONS[0]);
  const [recipient, setRecipient] = useState('');
  const [exportErr, setExportErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [proveNote, setProveNote] = useState('Just this entry, plus the hashes that link it to the chain head.');
  const [fileRes, setFileRes] = useState<{ ok: boolean; text: string } | null>(null);
  const [about, setAbout] = useState<Record<string, boolean>>({});
  const flip = (k: string) => setAbout((a) => ({ ...a, [k]: !a[k] }));

  useEffect(() => {
    if (intent.exportOpen || intent.query) onIntentUsed();
    // run once for the intent this tab opened with
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scope = fa ? undefined : (fac?.id ?? PLATFORM_SCOPE);
  const entries = useApi((c, signal) => ledgerEntries(c, { query: { ...(scope ? { scope } : {}), limit: 400 }, signal }), [scope], { pollMs: 5000 });
  const custody = useApi((c, signal) => ledgerCustody(c, { query: { ...(fa ? {} : fac ? { scope: fac.id } : {}), limit: 5 }, signal }), [fac?.id]);
  // Guardian cases are refused to the Partner and the Developer by the server
  // (v3/oversight/guardian-routes.ts), so their ledger does not ask for the strip.
  const guardian = useApi((c, signal) => oversightGuardianSignals(c, { query: { limit: 20 }, signal }), [], { enabled: role !== 'PARTNER' && role !== 'DEVELOPER' });

  const E = entries.data;
  const head = E?.head ?? null;
  const all = E?.entries ?? [];
  const term = q.trim().toLowerCase();
  const list = useMemo(
    () =>
      all
        .filter(
          (e) =>
            (kf === 'All' || e.kind === kf || (kf === 'People' && (e.kind === 'Override' || e.kind === 'Policy' || e.kind === 'Care log'))) &&
            (!term || ['LE-' + e.seq, e.id, e.what, e.actor, e.resident, e.hash].filter(Boolean).join(' ').toLowerCase().includes(term)),
        )
        .slice(0, 120),
    [all, kf, term],
  );
  const blocks = all.slice(0, 9);
  const sel = all.find((e) => e.id === selId) ?? null;
  const nextAnchor = 60 - new Date(now).getUTCMinutes();
  const anchorLive = !!E?.anchor.live;
  const facName = fa ? (all[0]?.facility?.name ?? session?.user.orgName ?? '') : '';

  const gRows = (guardian.data?.signals ?? [])
    .flatMap((s) => s.steps.filter((z) => z.ledgerRef).map((z) => ({ ...z, sid: s.id })))
    .sort((a, b) => Date.parse(b.at ?? '') - Date.parse(a.at ?? ''));

  const verify = async () => {
    if (ver?.s === 'running') return;
    const span = RANGE_MS[range];
    const to = new Date();
    const from = span === null ? new Date(0) : new Date(to.getTime() - span);
    setVer({ s: 'running', range });
    try {
      const r = await ledgerVerify(client, { body: { ...(scope ? { scope } : {}), from: from.toISOString(), to: to.toISOString() } });
      setVer({ s: 'done', range, at: to.toISOString().slice(11, 16) + ' UTC', r });
      custody.reload();
    } catch (e) {
      setVer({ s: 'error', msg: (e as Error).message });
    }
  };

  const prove = async (e: LedgerEntryRow) => {
    try {
      const proof = await ledgerProof(client, { params: { id: e.id } });
      const text = JSON.stringify(proof, null, 2);
      downloadJson('aithora-proof-LE-' + e.seq + '.json', proof);
      setProveNote('Downloaded · about ' + Math.max(1, Math.round(text.length / 1024)) + ' KB, not the whole ledger');
      custody.reload();
    } catch (err) {
      setProveNote((err as Error).message);
    }
  };

  const doExport = async () => {
    if (!recipient.trim() || busy) return;
    setBusy(true);
    setExportErr(null);
    const span = RANGE_MS[range];
    const to = new Date();
    const from = span === null ? new Date(0) : new Date(to.getTime() - span);
    try {
      const out = await ledgerExport(client, { body: { range: { from: from.toISOString(), to: to.toISOString() }, ...(scope ? { scope } : {}), reason, recipient: recipient.trim(), confirm: true } });
      downloadJson('aithora-evidence-' + to.toISOString().slice(0, 10) + '.json', { bundle: out.bundle, manifest: out.manifest, manifestJws: out.manifestJws, jwks: out.jwks });
      setExportOpen(false);
      custody.reload();
    } catch (err) {
      setExportErr((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onVerifyFile = async (f: File | undefined) => {
    if (!f) return;
    try {
      const j = JSON.parse(await f.text());
      const d = await sha256Hex(canonicalJson(j.bundle));
      const ok = !!j.manifest && j.manifest.sha256 === d;
      setFileRes({ ok, text: ok ? '✓ Intact: contents match the signed manifest (' + d.slice(0, 10) + '…)' : '✗ Altered: contents do not match the manifest' });
    } catch {
      setFileRes({ ok: false, text: '✗ Not an Aithora evidence file' });
    }
  };

  // The verify card (design tlVals verTag/verHead/verSub)
  let verTag = 'NOT VERIFIED THIS SESSION';
  let verHead = head ? 'Chain claims: intact through #' + head.seq : 'No sealed entries yet';
  let verSub = 'A claim is not evidence. Pick a range and press Verify to recompute every hash.';
  let tagC = '#3C454F';
  let wrapBg = 'rgba(34,39,46,.045)';
  if (ver?.s === 'running') {
    verTag = 'VERIFYING';
    verHead = 'Recomputing every entry · ' + ver.range.toLowerCase();
    verSub = 'Each entry’s hash is rebuilt from its contents and the entry before it.';
    tagC = '#264C7E';
    wrapBg = 'rgba(47,92,151,.07)';
  } else if (ver?.s === 'done' && !ver.r.valid) {
    verTag = 'BREAK FOUND';
    verHead = 'Entry #' + (ver.r.brokenAt?.seq ?? '?') + ' does not match what was written';
    verSub = (ver.r.storedHash && ver.r.recomputedHash ? 'Stored hash ' + short(ver.r.storedHash) + ', recomputed ' + short(ver.r.recomputedHash) + '. ' : (ver.r.reason ? ver.r.reason + '. ' : '')) + 'Everything after it is unproven until investigated.';
    tagC = '#B3261E';
    wrapBg = 'rgba(179,38,30,.07)';
  } else if (ver?.s === 'done') {
    verTag = 'VERIFIED · ' + ver.at;
    verHead = ver.r.checked.toLocaleString() + ' entries intact · ' + ver.range.toLowerCase();
    verSub = (ver.r.firstSeq !== null ? 'Every hash recomputed and matched, #' + ver.r.firstSeq + ' → #' + ver.r.lastSeq + '. ' : 'No entries in that range. ') + 'Written to the custody log.';
    tagC = '#1F6B52';
    wrapBg = 'rgba(31,107,82,.07)';
  } else if (ver?.s === 'error') {
    verTag = 'NOT VERIFIED';
    verHead = 'The check did not run';
    verSub = ver.msg;
  }
  const verRunning = ver?.s === 'running';
  const verDone = ver?.s === 'done' && ver.r.valid;
  const brokenSeq = ver?.s === 'done' && !ver.r.valid ? ver.r.brokenAt?.seq : undefined;

  const selFacts: [string, string, boolean][] = sel
    ? [
        ['Entry', 'LE-' + sel.seq, false],
        ['Actor', sel.actor ?? '–', false],
        ['Resident', sel.resident ?? '–', false],
        ['Facility', sel.facility?.name ?? '–', false],
        ['Hash', 'sha256:' + sel.hash, false],
        ['Previous', 'sha256:' + sel.previousHash, false],
        ['Anchored', anchorLive ? 'Time-stamped by ' + (E?.anchor.authority ?? 'the time-stamping authority') : 'Not yet: next anchor in ' + nextAnchor + ' min', !anchorLive],
        ['Device', 'Not captured yet', true],
        ['Network', 'Not captured yet', true],
      ]
    : [];

  return (
    <div data-screen-label="Truth Ledger" style={sx('flex:1 1 auto;min-height:0;display:flex;flex-direction:column;gap:9px;min-width:0;overflow-y:auto;overflow-x:hidden;position:relative;')}>
      <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:6px 18px;flex-wrap:wrap;min-width:0;')}>
        <LedgerTabs sub="chain" onSub={onSub} />
        {(
          [
            ['entries sealed', E ? (head?.seq ?? E.counts.total).toLocaleString() : '–'],
            ['sealed today', E ? E.counts.today.toLocaleString() : '–'],
            ['exports this month', E ? String(E.counts.exportsThisMonth) : '–'],
          ] as const
        ).map(([k, v]) => (
          <span key={k} style={sx('display:inline-flex;align-items:baseline;gap:6px;white-space:nowrap;')}>
            <span style={sx('font-size:20px;font-weight:800;letter-spacing:-.03em;font-variant-numeric:tabular-nums;color:#22272E;')}>{v}</span>
            <span style={sx('font-size:16px;color:#3C454F;')}>{k}</span>
          </span>
        ))}
        <span style={sx('flex:1 1 10px;')} />
        <select value={range} onChange={(e) => setRange(e.target.value as RangeName)} aria-label="Range to verify" style={sx('min-height:36px;border:1px solid rgba(34,39,46,.16);border-radius:9px;background:#FCFAF4;font-family:inherit;font-size:16px;font-weight:600;color:#22272E;padding:0 10px;')}>
          {RANGES.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        <Act onClick={verify} css="display:inline-flex;align-items:center;justify-content:center;min-height:36px;padding:0 14px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;background:#22272E;color:#FCFAF4;">
          {verRunning ? 'Verifying…' : 'Verify chain'}
        </Act>
        {canExport && (
          <Act onClick={() => setExportOpen(true)} disabled={!!roReason} title={roReason ?? undefined} css="display:inline-flex;align-items:center;justify-content:center;min-height:36px;padding:0 14px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;border:1px solid rgba(34,39,46,.2);color:#22272E;">
            Export evidence
          </Act>
        )}
      </div>
      {/* Active threats, with Mitigate and False positive (Aithora only; restored from the Flutter console). */}
      {wide && <ActiveThreats />}
      {/* Out-of-band confirmations and the outbound alert history (Aithora's operators only; restored from the Flutter console). */}
      {(role === 'SUPER_ADMIN' || role === 'OPERATOR') && <OobConfirmations />}
      {(role === 'SUPER_ADMIN' || role === 'OPERATOR') && <AlertHistory />}

      <div aria-label="Most recent sealed entries" style={sx('flex:0 0 auto;display:flex;align-items:center;gap:0;min-width:0;padding:4px 0 2px;')}>
        <span style={sx('flex:1 1 auto;min-width:0;overflow:hidden;display:flex;flex-direction:row-reverse;justify-content:flex-start;')}>
          {blocks.map((k, i) => {
            const bad = brokenSeq !== undefined && k.seq === brokenSeq;
            const checked = verDone || (ver?.s === 'done' && brokenSeq !== undefined && k.seq < brokenSeq);
            return (
              <span key={k.id} style={sx('display:flex;align-items:center;flex:1 0 108px;min-width:108px;')}>
                <span style={sx('flex:1 1 6px;min-width:6px;height:2px;background:' + (bad ? '#B3261E' : checked ? '#1F6B52' : 'rgba(34,39,46,.18)') + ';')} />
                <Act
                  onClick={() => setSelId(k.id)}
                  title={'LE-' + k.seq + ' · sha256 ' + k.hash}
                  css={
                    (i === 0 ? 'animation:riseIn .45s cubic-bezier(.22,.61,.36,1);' : '') +
                    'flex:0 0 auto;display:flex;flex-direction:column;align-items:center;gap:1px;padding:5px 8px;border-radius:7px;text-decoration:none;border:1.5px solid ' +
                    (bad ? '#B3261E' : checked ? '#1F6B52' : selId === k.id ? '#2F5C97' : 'rgba(34,39,46,.16)') +
                    ';background:' +
                    (bad ? 'rgba(179,38,30,.07)' : checked ? 'rgba(31,107,82,.07)' : 'rgba(34,39,46,.03)') +
                    ';transition:border-color .3s,background .3s;'
                  }
                >
                  <span style={sx('font-size:14px;font-weight:800;color:' + (bad ? '#B3261E' : '#22272E') + ';font-variant-numeric:tabular-nums;')}>#{k.seq}</span>
                  <span style={sx('font-size:14px;color:' + (bad ? '#B3261E' : '#54606C') + ';font-variant-numeric:tabular-nums;letter-spacing:.02em;')} translate="no">{short(k.hash)}</span>
                </Act>
              </span>
            );
          })}
        </span>
        <span style={sx('flex:0 0 auto;display:inline-flex;align-items:center;gap:6px;padding-left:10px;font-size:14px;font-weight:800;letter-spacing:.08em;color:#1F6B52;white-space:nowrap;')}>
          <span style={sx('width:8px;height:8px;border-radius:50%;background:#1F6B52;box-shadow:0 0 0 1px rgba(31,107,82,.18);transition:box-shadow .6s ease;')} />
          SEALING
        </span>
      </div>

      <div style={sx('flex:0 0 auto;display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,340px),1fr));gap:8px 22px;min-width:0;')}>
        <div role="status" style={sx('display:flex;align-items:center;gap:8px 16px;flex-wrap:wrap;padding:9px 12px;border-radius:10px;background:' + wrapBg + ';min-width:0;transition:background .3s;')}>
          <span style={sx('display:flex;flex-direction:column;gap:3px;flex:1 1 200px;min-width:0;')}>
            <BuildTagged tag={verTag}><span style={sx('font-size:14px;font-weight:800;letter-spacing:.1em;color:' + tagC + ';')}>{verTag}</span></BuildTagged>
            <span style={sx('font-size:16px;font-weight:800;color:#22272E;line-height:1.3;')}>{verHead}</span>
            <span style={sx('font-size:16px;color:#3C454F;line-height:1.35;')}>{verSub}</span>
            {verRunning && (
              <span style={sx('display:block;height:5px;border-radius:3px;background:rgba(34,39,46,.08);overflow:hidden;margin-top:3px;')}>
                <span style={sx('display:block;height:100%;width:40%;background:#2F5C97;animation:breathe 1.2s ease-in-out infinite;')} />
              </span>
            )}
          </span>
        </div>
        <div style={sx('display:flex;align-items:center;gap:8px 16px;flex-wrap:wrap;padding:9px 12px;border-radius:10px;background:rgba(34,39,46,.045);min-width:0;')}>
          <span style={sx('display:flex;flex-direction:column;gap:3px;flex:1 1 200px;min-width:0;')}>
            <span style={sx('font-size:14px;font-weight:800;letter-spacing:.1em;color:#3C454F;')}>EXTERNAL ANCHOR · RFC 3161</span>
            <span style={sx('font-size:16px;font-weight:800;color:#22272E;line-height:1.3;')}>{anchorLive ? 'Live · ' + (E?.anchor.authority ?? 'time-stamping authority') : 'Not live yet · timestamps are our own server clock'}</span>
            <span style={sx('font-size:16px;color:#3C454F;line-height:1.35;')}>
              {anchorLive
                ? 'Each hour’s chain head is signed by an outside time-stamping authority, so “when” holds up without trusting us.'
                : 'Once live, each hour’s chain head is signed by an outside time-stamping authority, so “when” holds up without trusting us. Open item for counsel.'}
            </span>
          </span>
          <span style={sx('font-size:16px;font-weight:800;color:#22272E;font-variant-numeric:tabular-nums;white-space:nowrap;')}>{'next batch ' + nextAnchor + 'm'}</span>
        </div>
      </div>

      <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:6px 14px;flex-wrap:wrap;min-width:0;border-bottom:1px solid rgba(34,39,46,.12);padding-bottom:7px;')}>
        <span role="group" aria-label="Kind" style={sx('display:inline-flex;border:1px solid rgba(34,39,46,.16);border-radius:9px;overflow:hidden;')}>
          {(['All', 'Decision', 'People', 'Escalation'] as const).map((v) => (
            <Act key={v} pressed={kf === v} onClick={() => setKf(v)} css={segBtn(kf === v)}>
              {v === 'People' ? 'People & policy' : v}
            </Act>
          ))}
        </span>
        {wide && <FacilitySearch value={fac} onPick={setFac} />}
        {!wide && <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{facName}</span>}
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search entry id, resident, actor, hash" aria-label="Search the ledger" style={sx('flex:1 1 200px;min-width:0;min-height:34px;border:1px solid rgba(34,39,46,.16);border-radius:9px;background:#FCFAF4;font-family:inherit;font-size:16px;color:#22272E;padding:0 10px;')} />
        <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>{list.length + ' shown · newest first'}</span>
      </div>

      <div style={sx('flex:1 0 400px;min-height:400px;display:flex;flex-wrap:wrap;align-items:stretch;gap:14px 24px;min-width:0;')}>
        <div role="table" aria-label="Ledger entries" style={sx('flex:1.65 1 460px;min-width:0;min-height:400px;display:flex;flex-direction:column;')}>
          <div role="row" style={sx('flex:0 0 auto;display:grid;grid-template-columns:62px 92px minmax(0,1fr) 150px;gap:10px;padding:0 8px 5px 11px;font-size:14px;letter-spacing:.1em;text-transform:uppercase;color:#54606C;font-weight:700;')}>
            <span>Time</span>
            <span>Entry</span>
            <span>What was recorded</span>
            <span style={sx('text-align:right;')}>Hash → previous</span>
          </div>
          <div style={sx('flex:1 1 0;min-height:340px;overflow-y:auto;overflow-x:hidden;display:flex;flex-direction:column;')}>
            {gRows.length > 0 && (
              <div style={sx('flex:0 0 auto;display:flex;flex-direction:column;padding:6px 0 8px;border-bottom:1px solid rgba(34,39,46,.12);min-width:0;')}>
                <span style={sx('font-size:14px;font-weight:800;letter-spacing:.08em;color:#B3261E;padding:0 0 4px 11px;')}>{'SEALED FROM GUARDIAN · ' + gRows.length + (gRows.length === 1 ? ' entry' : ' entries')}</span>
                {gRows.slice(0, 6).map((g) => (
                  <Link key={g.id} to="/guardian" style={sx('display:grid;grid-template-columns:62px 92px minmax(0,1fr) auto;gap:10px;align-items:center;min-height:32px;padding:0 8px 0 11px;text-decoration:none;color:inherit;border-radius:6px;')}>
                    <span style={sx('font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;')}>{g.at ? localClock(new Date(g.at)) : ''}</span>
                    <span style={sx('font-size:14px;font-weight:700;color:#22272E;font-variant-numeric:tabular-nums;')}>{g.ledgerRef}</span>
                    <span translate="no" style={sx('font-size:16px;color:#22272E;min-width:0;overflow-wrap:break-word;')}>
                      <b>{g.step.replace(/_/g, ' ')}</b> · {g.actor.name ?? 'Guardian signal'}
                    </span>
                    <span style={sx('font-size:14px;font-weight:700;color:#2F5C97;white-space:nowrap;')}>Open in Guardian →</span>
                  </Link>
                ))}
              </div>
            )}
            {E && list.length === 0 && <div style={sx('font-size:16px;color:#54606C;padding:16px 2px;')}>No entries match. The ledger itself is unchanged: filters only narrow what you see.</div>}
            {entries.error && <div style={sx('font-size:16px;color:#54606C;padding:16px 2px;')}>The sealed-entry list is not served yet ({entries.error.message}).</div>}
            {list.map((e) => (
              <Act
                key={e.id}
                onClick={() => setSelId(e.id)}
                pressed={selId === e.id}
                css={
                  'flex:0 0 auto;display:grid;grid-template-columns:62px 92px minmax(0,1fr) 150px;gap:10px;align-items:center;min-height:44px;padding:5px 8px 5px 8px;text-decoration:none;border-bottom:1px solid rgba(34,39,46,.07);border-left:3px solid ' +
                  (e.kind === 'Override' ? '#B3261E' : 'transparent') +
                  ';' +
                  (selId === e.id ? 'background:rgba(47,92,151,.08);' : '')
                }
              >
                <span style={sx('font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;')}>{localClock(new Date(e.at))}</span>
                <span style={sx('font-size:14px;font-weight:700;color:#22272E;font-variant-numeric:tabular-nums;')}>{'LE-' + e.seq}</span>
                <span style={sx('min-width:0;display:flex;flex-direction:column;gap:1px;')}>
                  <span style={sx('display:flex;align-items:center;gap:7px;min-width:0;')}>
                    <span style={sx(kStyle(e.kind))}>{e.kind}</span>
                    <span translate="no" style={sx('font-size:16px;font-weight:700;color:#22272E;line-height:1.3;min-width:0;')}>{e.what}</span>
                  </span>
                  <span translate="no" style={sx('font-size:14px;color:#54606C;line-height:1.3;')}>{[e.actor, e.resident, wide ? e.facility?.name : null].filter(Boolean).join(' · ')}</span>
                </span>
                <span style={sx('text-align:right;font-size:14px;color:#3C454F;font-variant-numeric:tabular-nums;white-space:nowrap;')}>
                  <span translate="no">{short(e.hash)}</span>
                  <span style={sx('display:block;color:#54606C;')} translate="no">← {short(e.previousHash)}</span>
                </span>
              </Act>
            ))}
          </div>
        </div>

        <div style={sx('flex:1 1 300px;min-width:0;min-height:400px;position:relative;border-left:1px solid rgba(34,39,46,.10);')}>
          <div style={sx('position:absolute;inset:0;overflow-y:auto;display:flex;flex-direction:column;gap:14px;padding-left:18px;')}>
            {/* V9: the explainers open on the "i" (design k16191, k16223, k16238). */}
            {!sel && (
              <AboutThis inline textStyle="font-size:16px;color:#54606C;line-height:1.45;">
                Pick any entry or chain block to see the full record, where it sits in the chain, and to download a proof for that one entry.
              </AboutThis>
            )}
            {sel && (
              <>
                <div style={sx('display:flex;flex-direction:column;gap:3px;')}>
                  <span style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
                    <span style={sx(kStyle(sel.kind))}>{sel.kind}</span>
                    <span style={sx('font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;')}>{new Date(sel.at).toISOString().replace('T', ' ').slice(0, 19) + ' UTC'}</span>
                  </span>
                  <span translate="no" style={sx('font-size:16px;font-weight:800;color:#22272E;line-height:1.3;')}>{sel.what}</span>
                  <span style={sx('font-size:16px;color:#3C454F;')}>
                    {'Position #' + sel.seq.toLocaleString() + ' · ' + (head && head.seq - sel.seq > 0 ? head.seq - sel.seq + ' entries before the newest' : 'the newest entry')}
                  </span>
                </div>
                <div style={sx('display:flex;flex-direction:column;gap:5px;')}>
                  {selFacts.map(([k, v, muted]) => (
                    <span key={k} style={sx('display:grid;grid-template-columns:96px minmax(0,1fr);gap:8px;font-size:14px;')}>
                      <span style={sx('color:#54606C;')}>{k}</span>
                      <span style={sx('font-weight:600;font-variant-numeric:tabular-nums;word-break:break-all;color:' + (muted ? '#54606C' : '#22272E') + ';')}>{v}</span>
                    </span>
                  ))}
                </div>
                <div style={sx('display:flex;align-items:center;gap:8px 14px;flex-wrap:wrap;')}>
                  <Act onClick={() => prove(sel)} css="display:inline-flex;align-items:center;justify-content:center;min-height:36px;padding:0 14px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;background:#2F5C97;color:#fff;">
                    Prove this entry
                  </Act>
                  <span style={sx('font-size:14px;color:#54606C;')}>{proveNote}</span>
                </div>
              </>
            )}
            <div style={sx('display:flex;flex-direction:column;')}>
              <span style={sx('font-size:14px;letter-spacing:.1em;text-transform:uppercase;color:#54606C;font-weight:700;padding-bottom:5px;border-bottom:1px solid rgba(34,39,46,.12);')}>Custody log · the ledger records who touched it</span>
              {custody.data && custody.data.entries.length === 0 && <span style={sx('font-size:14px;color:#54606C;padding:6px 0;')}>Nobody has verified, exported or proven anything yet.</span>}
              {(custody.data?.entries ?? []).slice(0, 5).map((c) => (
                <span key={c.id} style={sx('display:grid;grid-template-columns:minmax(0,1fr) auto;gap:2px 10px;padding:6px 0;border-bottom:1px solid rgba(34,39,46,.06);')}>
                  <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{custodyWhat(c)}</span>
                  <span style={sx('font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;text-align:right;')}>{whenText(c.at, now)}</span>
                  <span style={sx('grid-column:1 / -1;font-size:14px;color:#54606C;')}>{custodyWho(c)}</span>
                </span>
              ))}
            </div>
            <div style={sx('display:flex;flex-direction:column;gap:6px;padding:10px 12px;border-radius:10px;background:rgba(47,92,151,.06);')}>
              <span style={sx('font-size:14px;font-weight:800;letter-spacing:.1em;color:#264C7E;')}>
                VERIFY A FILE · NO LOGIN NEEDED
                <AboutButton open={!!about.verify} onToggle={() => flip('verify')} gap={6} />
              </span>
              {about.verify && <span style={sx('font-size:16px;color:#3C454F;line-height:1.4;')}>What a lawyer or regulator sees: drop an exported evidence file and the hashes are recomputed here, without trusting Aithora.</span>}
              <label style={sx('display:inline-flex;align-items:center;justify-content:center;min-height:40px;border:1.5px dashed rgba(47,92,151,.45);border-radius:9px;font-size:16px;font-weight:700;color:#264C7E;cursor:pointer;')}>
                Choose an evidence file (.json)
                <input type="file" accept=".json,application/json" onChange={(e) => onVerifyFile(e.target.files?.[0])} style={sx('display:none;')} />
              </label>
              {fileRes && <span style={sx('font-size:16px;font-weight:800;color:' + (fileRes.ok ? '#1F6B52' : '#B3261E') + ';')}>{fileRes.text}</span>}
            </div>
          </div>
        </div>
      </div>

      {exportOpen && (
        <div style={sx('position:fixed;inset:0;z-index:80;background:rgba(34,39,46,.32);display:flex;align-items:center;justify-content:center;padding:20px;')} onMouseDown={(e) => e.target === e.currentTarget && setExportOpen(false)}>
          <div role="dialog" aria-modal="true" aria-label="Export evidence" style={sx('width:min(100%,520px);background:#FCFAF4;border-radius:14px;padding:20px 22px;display:flex;flex-direction:column;gap:12px;box-shadow:0 18px 50px rgba(34,39,46,.25);')}>
            <span style={sx('font-size:18px;font-weight:800;color:#22272E;')}>
              Export evidence
              <AboutButton open={!!about.export} onToggle={() => flip('export')} gap={6} />
            </span>
            {about.export && (
              <span style={sx('font-size:16px;color:#3C454F;line-height:1.45;')}>
                A legal deliverable, not a spreadsheet. The file carries a chain-of-custody manifest (range, who, when, why, and a SHA-256 of the contents) so the recipient can prove nothing changed in transit. This export is itself written to the ledger.
              </span>
            )}
            <span style={sx('display:grid;grid-template-columns:90px minmax(0,1fr);gap:8px 10px;align-items:center;font-size:16px;')}>
              <span style={sx('color:#54606C;')}>Range</span>
              <span style={sx('font-weight:700;color:#22272E;')}>{range + ' · ' + (fa ? facName : fac ? fac.name : 'all facilities')}</span>
              <span style={sx('color:#54606C;')}>Reason</span>
              <select value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Reason" style={sx('min-height:36px;border:1px solid rgba(34,39,46,.16);border-radius:9px;background:#fff;font-family:inherit;font-size:16px;color:#22272E;padding:0 8px;')}>
                {REASONS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
              <span style={sx('color:#54606C;')}>Recipient</span>
              <input value={recipient} onChange={(e) => setRecipient(e.target.value)} aria-label="Recipient" placeholder="e.g. outside counsel, state surveyor" style={sx('min-height:36px;border:1px solid rgba(34,39,46,.16);border-radius:9px;background:#fff;font-family:inherit;font-size:16px;color:#22272E;padding:0 10px;')} />
              <span style={sx('color:#54606C;')}>Exported by</span>
              <span style={sx('font-weight:700;color:#22272E;')}>{[session?.user.name, role ? ROLE_LABEL[role] : null].filter(Boolean).join(' · ')}</span>
            </span>
            {exportErr && <span role="alert" style={sx('font-size:16px;font-weight:700;color:#B3261E;')}>{exportErr}</span>}
            <div style={sx('display:flex;align-items:center;gap:10px;justify-content:flex-end;flex-wrap:wrap;')}>
              <Act onClick={() => setExportOpen(false)} css="display:inline-flex;align-items:center;justify-content:center;min-height:36px;padding:0 14px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#3C454F;">
                Cancel
              </Act>
              <Act
                onClick={doExport}
                disabled={!recipient.trim() || busy || !!roReason}
                title={roReason ?? undefined}
                css={'display:inline-flex;align-items:center;justify-content:center;min-height:40px;padding:0 16px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' + (recipient.trim() && !roReason ? 'background:#22272E;color:#FCFAF4;' : 'background:rgba(34,39,46,.12);color:#54606C;')}
              >
                {busy ? 'Signing…' : 'Sign & download'}
              </Act>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
